import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Pagination } from "swiper/modules"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"

import TestimonialAuthor, {
  AuthorMeta,
  AuthorTitle,
  AuthorDash,
} from "./TestimonialAuthor"
import { Container, Row, Col } from "./Grid"

import { media, mixins } from "../styles"

const Wrapper = styled.div`
  background: ${({ theme }) => theme.greyLight};
  padding: 40px 20px 60px;

  @media ${media.md} {
    padding: 70px 10px 80px;
  }

  @media ${media.lg} {
    padding: 90px 10px 110px;
  }

  ${(props) =>
    props.$layout === "compact" &&
    css`
      background: none;

      @media ${media.lg} {
        padding: 0px 10px;
      }

      ${Header} {
        margin-bottom: 35px;

        h2 {
          ${mixins.title5}
        }
      }
    `}

  .swiper-pagination {
    @media ${media.md} {
      display: none;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    @media ${media.mdDown} {
      display: none;
    }
  }

  .swiper-button-disabled {
    display: none;
  }

  .swiper-button-next {
    right: 20px;

    @media ${media.lg} {
      right: 30px;
    }
  }

  .swiper-button-prev {
    left: 20px;

    @media ${media.lg} {
      left: 30px;
    }
  }
`

const Testimonial = styled.div`
  .swiper-slide & {
    padding-bottom: 60px;

    @media ${media.md} {
      padding-bottom: 0;
    }
  }

  ${(props) =>
    props.$layout === "compact" &&
    css`
      ${Quote} {
        ${mixins.copy3}
      }

      ${AuthorMeta},
      ${AuthorTitle} {
        ${mixins.copy4}
      }

      ${AuthorDash} {
        display: none;
      }
    `}
`

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media ${media.lg} {
    margin-bottom: 90px;
    padding: 0 60px;
  }

  h2 {
    ${mixins.title3}
  }
`

const Quote = styled.blockquote`
  ${mixins.copy1}
  text-align: center;
  padding: 0;
  margin: 0;
`

const Author = styled(TestimonialAuthor)`
  justify-content: center;
  margin: 30px 5px 0;

  @media ${media.md} {
    margin: 30px 0 0;
  }

  @media ${media.lg} {
    margin: 50px 0 0;
  }
`

const TestimonialHero = ({ testimonials, header, layout }) => {
  const sliderParams = {
    modules: [Navigation, Pagination],
    navigation: true,
    pagination: {
      type: "bullets",
      clickable: true,
    },
    slidesPerView: 1,
  }

  const renderTestimonial = (testimonial) => (
    <Testimonial $layout={layout}>
      <Row>
        <Col md="10" mdOffset="1" lg="8" lgOffset="2">
          <Quote>“{testimonial.text.text}”</Quote>
        </Col>
      </Row>
      <Row>
        <Col md="10" mdOffset="1" lg="8" lgOffset="2">
          <Author
            name={testimonial.authorName}
            photo={testimonial.authorPhoto}
            title={testimonial.authorTitle}
          />
        </Col>
      </Row>
    </Testimonial>
  )

  const renderSlider = (testimonials) => (
    <Swiper {...sliderParams}>
      {testimonials.map((testimonial, i) => (
        <SwiperSlide key={i}>{renderTestimonial(testimonial)}</SwiperSlide>
      ))}
    </Swiper>
  )

  return (
    <Container>
      <Wrapper $layout={layout}>
        {header && (
          <Row>
            <Col sm="6" smOffset="3">
              <Header>
                <h2>{header}</h2>
              </Header>
            </Col>
          </Row>
        )}
        {testimonials.length > 1
          ? renderSlider(testimonials)
          : renderTestimonial(testimonials[0])}
      </Wrapper>
    </Container>
  )
}

TestimonialHero.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      authorName: PropTypes.string.isRequired,
      authorPhoto: PropTypes.object,
      authorTitle: PropTypes.string,
      text: PropTypes.shape({
        text: PropTypes.string.isRequired,
      }).isRequired,
    })
  ),
  header: PropTypes.string,
  layout: PropTypes.string,
}

export default TestimonialHero
